// Readable, safe view of submitted JSON (design §5): a key/value list with
// nested objects indented, long strings cut short with "Show all", and long
// lists paged. Everything renders as React text — never as HTML — so a
// report can't smuggle markup into Goblic's own screens.

import { useState } from 'react'

const MAX_STRING = 160
const MAX_ITEMS = 50
const MAX_DEPTH = 8

export function ReportDataView({ data }: { data: unknown }) {
  if (data === null || typeof data !== 'object') {
    return (
      <div className="text-base text-ink">
        <Scalar value={data} />
      </div>
    )
  }
  return (
    <div className="text-base">
      <Entries value={data as object} depth={0} />
    </div>
  )
}

function Entries({ value, depth }: { value: object; depth: number }) {
  const [showAll, setShowAll] = useState(false)
  const isArray = Array.isArray(value)
  const entries: [string, unknown][] = isArray
    ? (value as unknown[]).map((item, index) => [String(index + 1), item])
    : Object.entries(value)

  if (entries.length === 0) {
    return <span className="text-ink-3">{isArray ? '(empty list)' : '(empty)'}</span>
  }
  const visible = showAll ? entries : entries.slice(0, MAX_ITEMS)

  return (
    <dl className="space-y-1">
      {visible.map(([key, item]) => {
        const nested = item !== null && typeof item === 'object'
        return (
          <div key={key} className={nested ? '' : 'flex min-w-0 flex-wrap gap-x-2'}>
            <dt
              className={`shrink-0 break-all font-medium ${isArray ? 'text-ink-3' : 'text-ink-2'}`}
            >
              {isArray ? `${key}.` : `${key}:`}
            </dt>
            <dd className={`min-w-0 text-ink ${nested ? 'ml-4 mt-1 border-l border-line pl-3' : ''}`}>
              {nested ? (
                depth + 1 >= MAX_DEPTH ? (
                  <LongText text={safeStringify(item)} mono />
                ) : (
                  <Entries value={item as object} depth={depth + 1} />
                )
              ) : (
                <Scalar value={item} />
              )}
            </dd>
          </div>
        )
      })}
      {entries.length > MAX_ITEMS && !showAll ? (
        <button
          type="button"
          className="btn-ghost btn-sm"
          onClick={() => setShowAll(true)}
        >
          Show {entries.length - MAX_ITEMS} more
        </button>
      ) : null}
    </dl>
  )
}

function Scalar({ value }: { value: unknown }) {
  if (value === null) return <span className="text-ink-3">null</span>
  if (typeof value === 'string') {
    if (value === '') return <span className="text-ink-3">(empty text)</span>
    return <LongText text={value} />
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return <span className="font-mono text-sm">{String(value)}</span>
  }
  return <span className="text-ink-3">{String(value)}</span>
}

function LongText({ text, mono }: { text: string; mono?: boolean }) {
  const [expanded, setExpanded] = useState(false)
  const long = text.length > MAX_STRING
  const shown = long && !expanded ? `${text.slice(0, MAX_STRING)}…` : text
  return (
    <span className={`whitespace-pre-wrap break-words ${mono ? 'font-mono text-sm' : ''}`}>
      {shown}
      {long ? (
        <button
          type="button"
          className="link ml-2 text-sm"
          onClick={() => setExpanded((open) => !open)}
        >
          {expanded ? 'Show less' : `Show all (${text.length} characters)`}
        </button>
      ) : null}
    </span>
  )
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value) ?? String(value)
  } catch {
    return String(value)
  }
}
